// lib/koi-pond-v3/quality/damp.ts
//
// Frame-rate-independent motion helpers. The fluidity audit found the cursor
// lens used a per-FRAME lerp (×0.06), so it eased ~2× faster on a 120Hz display
// than on 60Hz. These are dt-based, so the time constant is identical at any
// refresh rate (true 120fps motion on high-refresh panels, no 60-lock).

import { Vector2 } from 'three';

/**
 * Exponential smoothing toward a target — first-order, no overshoot, stable at
 * any dt. `rate` is in 1/seconds (higher = snappier). rate≈3.7 reproduces the
 * old ×0.06-per-frame feel at 60fps.
 */
export function expDamp(current: number, target: number, rate: number, dt: number): number {
  return current + (target - current) * (1 - Math.exp(-rate * dt));
}

/**
 * Critically-damped 2D spring (no overshoot, smooth momentum follow). Uses the
 * closed-form critically-damped solution, so it's unconditionally stable
 * regardless of dt (frame drops can't blow it up). `omega` is the angular
 * frequency (higher = tighter follow).
 */
export class Spring2 {
  readonly value = new Vector2();
  private readonly vel = new Vector2();
  private readonly omega: number;

  constructor(omega = 12) {
    this.omega = omega;
  }

  set(x: number, y: number): void {
    this.value.set(x, y);
    this.vel.set(0, 0);
  }

  /** Step toward (tx, ty) over dt seconds; returns the (mutated) value vector. */
  update(tx: number, ty: number, dt: number): Vector2 {
    const w = this.omega;
    const e = Math.exp(-w * dt);
    // y = value - target; critically damped: y(t) = (y0 + (v0 + w·y0)·t)·e^{-w t}
    const y0x = this.value.x - tx;
    const y0y = this.value.y - ty;
    const cx = this.vel.x + w * y0x;
    const cy = this.vel.y + w * y0y;
    this.value.x = tx + (y0x + cx * dt) * e;
    this.value.y = ty + (y0y + cy * dt) * e;
    this.vel.x = (this.vel.x - w * dt * cx) * e;
    this.vel.y = (this.vel.y - w * dt * cy) * e;
    return this.value;
  }
}
